import { Link } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import { Checkbox } from '@workspace/ui/components/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { Input } from '@workspace/ui/components/input'
import { ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { QueryGuard } from '@/components/query-guard'
import { useAdminDeleteUserMutation, useAdminUserDeletionPreviewQuery } from '@/hooks/api'

interface DeleteUserDialogProps {
  userId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted: () => void
}

export function DeleteUserDialog({ userId, open, onOpenChange, onDeleted }: DeleteUserDialogProps) {
  const previewQuery = useAdminUserDeletionPreviewQuery(userId, { enabled: open })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
          <DialogDescription>
            This action is irreversible. All selected data will be permanently deleted.
          </DialogDescription>
        </DialogHeader>
        <QueryGuard query={previewQuery} errorMessage="Failed to load deletion preview">
          {(preview) => (
            <DeleteUserDialogContent
              preview={preview}
              onCancel={() => onOpenChange(false)}
              onDeleted={onDeleted}
            />
          )}
        </QueryGuard>
      </DialogContent>
    </Dialog>
  )
}

type Preview = {
  user: { id: string; name: string | null; email: string }
  conversations: { id: string; name: string }[]
  documents: { id: string; fileName: string }[]
  pitchSessions: { id: string; name: string }[]
  feedback: number
  dataRoomViews: number
  dataRoomSessions: number
  solelyOwnedStartups: {
    id: string
    name: string
    documents: { id: string; fileName: string }[]
    dataRooms: { id: string; name: string }[]
    pitchSessions: { id: string; name: string }[]
    prescreens: { id: string; name: string }[]
    creditTransactions: number
  }[]
  solelyOwnedInvestors: {
    id: string
    name: string
    pitchSessions: { id: string; name: string }[]
    prescreens: { id: string; name: string }[]
    creditTransactions: number
    personas: number
  }[]
  sharedStartups: { id: string; name: string }[]
  sharedInvestors: { id: string; name: string }[]
}

function DeleteUserDialogContent({
  preview,
  onCancel,
  onDeleted,
}: {
  preview: Preview
  onCancel: () => void
  onDeleted: () => void
}) {
  const [selectedStartupIds, setSelectedStartupIds] = useState<Set<string>>(
    new Set(preview.solelyOwnedStartups.map((s) => s.id))
  )
  const [selectedInvestorIds, setSelectedInvestorIds] = useState<Set<string>>(
    new Set(preview.solelyOwnedInvestors.map((i) => i.id))
  )
  const [confirmEmail, setConfirmEmail] = useState('')

  const deleteMutation = useAdminDeleteUserMutation({
    onSuccess: () => onDeleted(),
  })

  const canConfirm = confirmEmail === preview.user.email && !deleteMutation.isPending

  const handleDelete = () => {
    deleteMutation.mutate({
      path: { id: preview.user.id },
      body: {
        deleteStartupIds: Array.from(selectedStartupIds),
        deleteInvestorIds: Array.from(selectedInvestorIds),
      },
    })
  }

  const toggleStartup = (id: string) => {
    setSelectedStartupIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleInvestor = (id: string) => {
    setSelectedInvestorIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-4">
      {/* User info */}
      <div className="rounded-md border border-border p-3 text-sm">
        <p className="font-medium">
          {preview.user.name || 'Unnamed'} ({preview.user.email})
        </p>
      </div>

      {/* User's direct data */}
      <div className="space-y-1">
        <p className="text-sm font-medium">User data to delete</p>
        <div className="rounded-md border border-border divide-y divide-border text-sm">
          <ItemSection
            label="Onboarding conversations"
            items={preview.conversations}
            linkTo="/admin/onboarding/$id"
          />
          <ItemSection
            label="Documents"
            items={preview.documents.map((d) => ({ id: d.id, name: d.fileName }))}
          />
          <ItemSection
            label="Evaluations"
            items={preview.pitchSessions}
            linkTo="/admin/evaluations/$id"
          />
          <CountRow label="Feedback" count={preview.feedback} />
          <CountRow label="Data room views" count={preview.dataRoomViews} />
          <CountRow label="Data room sessions" count={preview.dataRoomSessions} />
        </div>
      </div>

      {/* Solely owned startups */}
      {preview.solelyOwnedStartups.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Solely owned startups</p>
          {preview.solelyOwnedStartups.map((startup) => (
            <div key={startup.id} className="rounded-md border border-border text-sm">
              <label
                htmlFor={`startup-${startup.id}`}
                className="flex items-start gap-2 p-2 cursor-pointer"
              >
                <Checkbox
                  id={`startup-${startup.id}`}
                  checked={selectedStartupIds.has(startup.id)}
                  onCheckedChange={() => toggleStartup(startup.id)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Link
                    to="/admin/startups/$id"
                    params={{ id: startup.id }}
                    className="font-medium text-[#f97316] hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {startup.name}
                  </Link>
                </div>
              </label>
              {selectedStartupIds.has(startup.id) && (
                <div className="border-t border-border divide-y divide-border">
                  <ItemSection
                    label="Documents"
                    items={startup.documents.map((d) => ({ id: d.id, name: d.fileName }))}
                  />
                  <ItemSection
                    label="Data rooms"
                    items={startup.dataRooms}
                    linkTo="/admin/datarooms/$id"
                  />
                  <ItemSection
                    label="Evaluations"
                    items={startup.pitchSessions}
                    linkTo="/admin/evaluations/$id"
                  />
                  <ItemSection
                    label="Prescreens"
                    items={startup.prescreens}
                    linkTo="/admin/prescreens/$id"
                  />
                  <CountRow label="Credit transactions" count={startup.creditTransactions} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Solely owned investors */}
      {preview.solelyOwnedInvestors.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Solely owned investor profiles</p>
          {preview.solelyOwnedInvestors.map((investor) => (
            <div key={investor.id} className="rounded-md border border-border text-sm">
              <label
                htmlFor={`investor-${investor.id}`}
                className="flex items-start gap-2 p-2 cursor-pointer"
              >
                <Checkbox
                  id={`investor-${investor.id}`}
                  checked={selectedInvestorIds.has(investor.id)}
                  onCheckedChange={() => toggleInvestor(investor.id)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Link
                    to="/admin/investors/$id"
                    params={{ id: investor.id }}
                    className="font-medium text-[#f97316] hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {investor.name}
                  </Link>
                </div>
              </label>
              {selectedInvestorIds.has(investor.id) && (
                <div className="border-t border-border divide-y divide-border">
                  <ItemSection
                    label="Evaluations"
                    items={investor.pitchSessions}
                    linkTo="/admin/evaluations/$id"
                  />
                  <ItemSection
                    label="Prescreens"
                    items={investor.prescreens}
                    linkTo="/admin/prescreens/$id"
                  />
                  <CountRow label="Credit transactions" count={investor.creditTransactions} />
                  <CountRow label="Personas" count={investor.personas} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Shared memberships */}
      {(preview.sharedStartups.length > 0 || preview.sharedInvestors.length > 0) && (
        <div className="rounded-md border border-border p-3 text-sm">
          <p className="font-medium text-muted-foreground">Membership will be removed from:</p>
          <ul className="mt-1 list-disc pl-4 text-muted-foreground">
            {preview.sharedStartups.map((s) => (
              <li key={s.id}>
                Startup:{' '}
                <Link
                  to="/admin/startups/$id"
                  params={{ id: s.id }}
                  className="text-[#f97316] hover:underline"
                >
                  {s.name}
                </Link>
              </li>
            ))}
            {preview.sharedInvestors.map((i) => (
              <li key={i.id}>
                Investor:{' '}
                <Link
                  to="/admin/investors/$id"
                  params={{ id: i.id }}
                  className="text-[#f97316] hover:underline"
                >
                  {i.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Email confirmation */}
      <div className="space-y-2">
        <label htmlFor="confirm-email" className="text-sm">
          Type <span className="font-mono font-medium">{preview.user.email}</span> to confirm
        </label>
        <Input
          id="confirm-email"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder={preview.user.email}
        />
      </div>

      {deleteMutation.isError && (
        <p className="text-sm text-red-500">Failed to delete user. Please try again.</p>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={deleteMutation.isPending}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={handleDelete} disabled={!canConfirm}>
          {deleteMutation.isPending ? 'Deleting...' : 'Delete User'}
        </Button>
      </DialogFooter>
    </div>
  )
}

type AdminDetailRoute =
  | '/admin/onboarding/$id'
  | '/admin/evaluations/$id'
  | '/admin/datarooms/$id'
  | '/admin/prescreens/$id'
  | '/admin/startups/$id'
  | '/admin/investors/$id'

function ItemSection({
  label,
  items,
  linkTo,
}: {
  label: string
  items: { id: string; name: string }[]
  linkTo?: AdminDetailRoute
}) {
  if (items.length === 0) {
    return (
      <div className="flex items-center justify-between px-3 py-1.5 text-muted-foreground">
        <span>{label}</span>
        <span>0</span>
      </div>
    )
  }

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-1.5 hover:bg-muted/50 group">
        <span>{label}</span>
        <span className="flex items-center gap-1 text-muted-foreground">
          {items.length}
          <ChevronRight className="size-3 transition-transform group-data-[state=open]:rotate-90" />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="px-3 pb-1.5 space-y-0.5">
          {items.map((item) => (
            <li key={item.id} className="text-xs text-muted-foreground truncate">
              {linkTo ? (
                <Link
                  to={linkTo}
                  params={{ id: item.id }}
                  className="text-[#f97316] hover:underline"
                >
                  {item.name}
                </Link>
              ) : (
                item.name
              )}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}

function CountRow({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 text-muted-foreground">
      <span>{label}</span>
      <span>{count}</span>
    </div>
  )
}
